import { make } from 'vuex-pathify';
import { filter, keyBy, keys, reject, shuffle, sortBy, values } from 'lodash';

const steps = keyBy(
    [
        {
            id: 1,
            text:
                'Check the expiration date on the medication vial. If date has passed, do not use medication. Check syringe package. If the package has been opened or damaged, do not use the syringe. Make sure you have the right-size needles for drawing the hormones and doing the subcutaneous or intramuscular injection.',
            image: require('@/assets/injectionSteps/injectionsteps_step1.png'),
            imageText: 'Medication vial w/ expiration date',
            answered: false,
            visited: false
        },
        {
            id: 2,
            text: 'Wash your hands',
            image: require('@/assets/injectionSteps/injectionsteps_step2.png'),
            imageText: 'Washing hands',
            answered: false,
            visited: false
        },
        {
            id: 3,
            text: 'Clean the top of the bottle with an alcohol pad for 20 seconds and let it air dry for 10 seconds. Throw away the pad. ',
            image: require('@/assets/injectionSteps/injectionsteps_step3.png'),
            imageText: 'Alcohol pad wiping top of vial',
            answered: false,
            visited: false
        },
        {
            id: 4,
            text:
                'Fill the syringe with the amount of air equal to the amount of hormones you’ll be using.  This makes it easier to get the hormones out of the bottle.',
            image: require('@/assets/injectionSteps/injectionsteps_step4.png'),
            imageText: 'Drawing air into syringe',
            answered: false,
            visited: false
        },
        {
            id: 5,
            text:
                'Insert the needle, with syringe, into the stopper. Turn the bottle upside down, and push the air into the vial. Once the air is out, move the needle back into the fluid, and pull the plunger back to draw up the right amount of hormones.  Flip the vial over and pull the needle out of the bottle and recap. Replace the needle with the correct size needle for your injection.',
            image: require('@/assets/injectionSteps/injectionsteps_step5.png'),
            imageText: 'Putting syringe into vial',
            answered: false,
            visited: false
        },
        {
            id: 6,
            text:
                'Tap syringe to make any air bubbles rise to the top.  Push out the extra air with a little dribble of hormones to make sure there is no air in the needle.',
            image: require('@/assets/injectionSteps/injectionsteps_step6.png'),
            imageText: 'Tapping syringe with fingers',
            answered: false,
            visited: false
        },
        {
            id: 7,
            text:
                'Intramuscular injections: After preparing the shot, prepare to inject into the butt or thigh muscle. If you’re doing the butt, the safe area is the top right or left upper portion, depending on if it’s the right or left butt cheek, respectively. (Like if you divided a butt cheek into 4 quarters—do the upper-right on the right butt cheek and the upper-left on the left butt cheek – look at the green area in the pic). Get your leg comfortable, straight and relaxed to get the tension out of your muscle so it will hurt less. Subcutaneous injections: After preparing the shot, prepare to inject into the abdomen or thighs. Stay at least two finger widths away from the belly button.',
            image: require('@/assets/injectionSteps/injectionsteps_step7.png'),
            imageText: 'Thigh and butt injection areas',
            answered: false,
            visited: false
        },
        {
            id: 8,
            text:
                'Clean the injection site with an alcohol pad in a spiral motion (from inside to out) to push dirt and germs away from the injection site. If you don’t have an alcohol pad, use soap and water.  Let the skin air dry.',
            image: require('@/assets/injectionSteps/injectionsteps_step8.png'),
            imageText: 'Using alcohol pad to wipe thigh',
            answered: false,
            visited: false
        },
        {
            id: 9,
            text:
                'Hold the skin around the injection site by pinching up a section of tissue about an inch wide. Holding the barrel, push the needle in with one quick jab.  For intramuscular injections, the needle should be at a 90-degree angle—straight in—not at an angle. For subcutaneous injections, the needle can be at a 45- or 90-degree angle.',
            image: require('@/assets/injectionSteps/injectionsteps_step9.png'),
            imageText: 'Pinching skin and needle at 90 degree angle',
            answered: false,
            visited: false
        },
        {
            id: 10,
            text:
                'Draw back on the syringe.  If you don’t see any blood in the barrel, inject slowly. If you see blood it means you hit a vein or artery, so pull the needle out and try again.',
            image: require('@/assets/injectionSteps/injectionsteps_step10.png'),
            imageText: 'Injecting medicine',
            answered: false,
            visited: false
        },
        {
            id: 11,
            text:
                'After injecting, pull the needle and syringe out and dispose of it safely. Gently press clean cotton ball on the injection site, holding pressure until there is no bleeding. You can use a band aid if needed.',
            image: require('@/assets/injectionSteps/injectionsteps_step11.png'),
            imageText: 'Throwing needle into sharps container',
            answered: false,
            visited: false
        }
    ],
    'id'
);
const stepsOrder = sortBy(keys(steps), k => parseInt(k));
const currentStep = 1;

const goalBlurbs = {
    Health:
        'You learned a lot of information today about keeping yourself healthier and safer. No matter what your health goals are, practicing safer sex and injection use can help you meet your goals!',
    Education:
        'No matter what your educational goals are, it is important to take steps to be healthier and safer. Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!',
    Career:
        'No matter what your career goals are, it is important to take steps to be healthier and safer. Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!',
    Relationships:
        'Practicing safer sex is an important part of having healthy relationships with any sex partner. Sometimes it might be hard to assert yourself around things like condom use. Let’s be real, the longer you’ve been hooking up with someone the harder it can get!  But having safe sex is one step you can take towards your relationship goals!',
    Finances:
        'No matter what your financial goals are, it is important to take steps to be healthier and safer. Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!',
    Stability:
        'No matter what your stability goals are, it is important to take steps to be healthier and safer. Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!',
    Hobbies:
        'No matter what your hobby-related goals are, it is important to take steps to be healthier and safer. Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!',
    Other: 'Practicing safer sex and injection use are two important steps you can take towards meeting all your goals!'
};

const state = {
    title: 'Safer Injection Steps',
    complete: false,
    sticker: {
        text: 'Thumbs up emoji',
        earned: false,
        image: require('@/assets/stickers/stickers_0015_thumbsup.png')
    },
    bonusSticker: false,
    trophy: {
        text: 'Light bulb',
        earned: false,
        image: require('@/assets/trophies/trophy_0002_lightbulb.png')
    },
    steps,
    stepsOrder,
    currentStep,
    goalBlurbs
};
const getters = {
    ...make.getters(state),
    allStepsAnswered: state => {
        return 0 === filter(values(state.steps), { answered: false }).length;
    },
    allStepsVisited: state => {
        return 0 === filter(values(state.steps), { visited: false }).length;
    },
    answerChoices: state => {
        const otherSteps = shuffle(reject(values(state.steps), { id: state.currentStep })); // shuffle all the steps that aren't the current step
        return shuffle([state.steps[state.currentStep], otherSteps.pop(), otherSteps.pop()]); // return 3 shuffled choices: 1 correct, 2 random incorrect
    }
};
const mutations = {
    ...make.mutations(state),
    nextStep(state) {
        state.currentStep++;
    }
};
const actions = make.actions(state);

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
